import { Link } from '@inertiajs/react'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table'
import { t } from '@/shared/lib/i18n'
import { reportsMonthlyPath } from '@/shared/lib/routes'
import { currentMonthStart, formatCalendarDate } from '@/shared/lib/tenantDateTime'

interface PendingSignoffRow {
  departmentId: number
  departmentName: string
  siteName: string | null
  pendingCount: number
  oldestPendingDate: string | null
}

interface PendingSignoffsTableProps {
  pendingSignoffsByDepartment: PendingSignoffRow[]
}

const monthlyReportsHref = (row: PendingSignoffRow): string => {
  const params: Record<string, string | number> = {
    department_id: row.departmentId,
  }
  if (row.oldestPendingDate) {
    params.month_from = currentMonthStart(row.oldestPendingDate)
  }
  return reportsMonthlyPath(params)
}

const PendingSignoffsTable = ({ pendingSignoffsByDepartment }: PendingSignoffsTableProps) => {
  return (
    <div className="space-y-3">
      <h2 className="text-lg font-semibold">
        {t('admin.signoffMonitoring.pendingSignoffs.sectionTitle')}
      </h2>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>
                {t('admin.signoffMonitoring.pendingSignoffs.columns.department')}
              </TableHead>
              <TableHead className="w-24">
                {t('admin.signoffMonitoring.pendingSignoffs.columns.pendingCount')}
              </TableHead>
              <TableHead className="w-36">
                {t('admin.signoffMonitoring.pendingSignoffs.columns.oldestDate')}
              </TableHead>
              <TableHead className="w-20" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {pendingSignoffsByDepartment.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center text-muted-foreground">
                  {t('admin.signoffMonitoring.pendingSignoffs.emptyState')}
                </TableCell>
              </TableRow>
            ) : (
              pendingSignoffsByDepartment.map((row) => (
                <TableRow key={row.departmentId}>
                  <TableCell className="font-medium">
                    {row.departmentName}
                    {row.siteName && (
                      <p className="text-sm font-normal text-muted-foreground">{row.siteName}</p>
                    )}
                  </TableCell>
                  <TableCell className="tabular-nums font-semibold">{row.pendingCount}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {row.oldestPendingDate ? formatCalendarDate(row.oldestPendingDate) : '—'}
                  </TableCell>
                  <TableCell>
                    <Link
                      href={monthlyReportsHref(row)}
                      className="text-xs text-primary hover:underline"
                    >
                      {t('admin.signoffMonitoring.pendingSignoffs.viewDashboard')}
                    </Link>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}

export default PendingSignoffsTable
